import React, { ChangeEvent, useState } from 'react';
import { Line } from 'rc-progress';
import { Close } from '@menuzen/icon/build/outline';
import ImageIcon from '@menuzen/icon/build/outline/ImageIcon';
import classNames from 'classnames';
import { ApiError } from '../../../shared/types/api-response';
import { resizeImage } from '../../../shared/utils/image';
import * as Sentry from '@sentry/react';
import { useUpload } from '../../../common/hooks/use-upload';

export interface UploadImageInputProps {
    value: string | undefined;
    onChange: (value: string) => void;
    imageClassName?: string;
    className?: string;
    maxSize?: number;
    requestId?: string;
}
export const UploadImageInput: React.FC<UploadImageInputProps> = ({
    value,
    onChange,
    imageClassName = 'w-full h-full object-cover rounded-5',
    className,
    maxSize = 10000000, // default 10MB
    ...props
}) => {
    const [progress, setProgress] = useState<null | number>(null);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const { uploadImage } = useUpload();

    const handleUploadImage = async (e: ChangeEvent<HTMLInputElement>) => {
        const formData = new FormData();
        const { files } = e.target;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const file = files.item(0);
        if (!file) {
            setErrorMessage('Please select file');
            e.target.value = '';
            return;
        }
        // Resize image
        let resizedImage = null;
        try {
            resizedImage = await resizeImage(file);
        } catch (e) {
            Sentry.captureException(e);
        }
        if (!resizedImage) {
            setErrorMessage('Cannot upload image.');
            return;
        }

        formData.append('file', resizedImage as unknown as File);
        // Validate size
        if (resizedImage && file.size > maxSize) {
            setErrorMessage('File too large. Please upload a smaller image.');
            e.target.value = '';
            return;
        }
        e.target.value = '';
        setErrorMessage('');
        try {
            const res = await uploadImage(file, {
                onUploadProgress: (val: number | null) => setProgress(val),
            });
            if (onChange && res) onChange(res);
            setProgress(null);
        } catch (error: unknown) {
            const { message, status } = error as ApiError;
            if (message) {
                if (status === 400) {
                    setErrorMessage('File type not supported. Please upload ad .jpg, .jpeg or .png');
                } else {
                    setErrorMessage(message);
                }
            }
        }
    };
    return (
        <>
            <div
                className={classNames([
                    className,
                    'relative overflow-hidden border border-dashed border-primary-50 rounded-5 flex flex-col items-center justify-center',
                ])}
                {...props}
            >
                {progress !== null ? (
                    <Line
                        percent={progress}
                        strokeWidth={2}
                        trailWidth={2}
                        trailColor={'#F1F2F4'}
                        strokeColor="#55D1D3"
                        className={'mx-20'}
                    />
                ) : (
                    <>
                        <input
                            className={'visible-hidden opacity-0 absolute top-0 left-0 w-full h-full'}
                            type="file"
                            accept={'image/*'}
                            onChange={(event) => {
                                handleUploadImage(event);
                            }}
                        />
                        {value ? (
                            <>
                                <div
                                    className="absolute right-10 top-10 text-error rounded-full"
                                    onClick={() => {
                                        onChange && onChange('');
                                    }}
                                >
                                    <Close className="text-error" />
                                </div>
                                <div className="flex justify-center items-center h-full w-full">
                                    <img src={value} className={imageClassName} />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="text-20 text-primary-50">
                                    <ImageIcon />
                                </div>
                                <p className="font-semibold mt-10 leading-15 text-primary-50">Upload Image</p>
                            </>
                        )}
                    </>
                )}
            </div>
            {errorMessage && <div className={'mt-10 text-error italic'}>{errorMessage}</div>}
        </>
    );
};
